import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";

const ActivityMappingModal = ({ data, onClose, onSave }) => {
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // ── Pre-select already mapped activity IDs ────────────────
  useEffect(() => {
    if (data?.activity_ids) {
      const ids = new Set(
        data.activity_ids
          .split(",")
          .map((id) => Number(id.trim()))
          .filter(Boolean)
      );
      setSelectedIds(ids);
      setOriginalIds(ids);
    } else {
      setSelectedIds(new Set());
      setOriginalIds(new Set());
    }
  }, [data]);

  // ── Fetch activity structure ──────────────────────────────
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoadingActivities(true);
        const res = await apiClient.get("/api/get-activity-structure", {
          params: {
                reporting_year_id: data.id,
            },
        });
        const cats = res.data?.data || [];
        setCategories(cats);
        // Expand all categories by default
        setExpandedCategories(new Set(cats.map((c) => c.id)));
      } catch (err) {
        console.error("Activity structure fetch error:", err);
        setError("Failed to load activities.");
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [data.id]);

  // ── Toggle single sub-activity ────────────────────────────
  const handleCheckbox = (subId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(subId) ? next.delete(subId) : next.add(subId);
      return next;
    });
  };

  // ── Toggle all sub-activities in a category ───────────────
  const handleCategoryToggle = (cat) => {
    const subIds = cat.subactivities.map((s) => s.id);
    const allChecked = subIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        subIds.forEach((id) => next.delete(id));
      } else {
        subIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // ── Toggle category accordion ─────────────────────────────
  const toggleExpand = (catId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(catId) ? next.delete(catId) : next.add(catId);
      return next;
    });
  };

  // ── Select / Deselect all ─────────────────────────────────
  const allSubIds = categories.flatMap((c) => c.subactivities.map((s) => s.id));
  const allSelected =
    allSubIds.length > 0 && allSubIds.every((id) => selectedIds.has(id));

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allSubIds));
    }
  };

  // ── Submit — diff checked vs unchecked ───────────────────
  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      const checked_ids = [...selectedIds];
      const unchecked_ids = allSubIds.filter((id) => !selectedIds.has(id));

      const res = await apiClient.post(
        "/api/activitieupdate",
        {
            "checked_ids":checked_ids,
            "unchecked_ids":unchecked_ids,
            "reportingyear_id": data.id,
        }
        );

      if (!(res.data?.code === 200 || res.data?.success)) {
        throw new Error(res.data?.message || "Mapping failed");
      }

      onSave(res.data?.message || "Activities mapped successfully");
    } catch (err) {
      console.error("Activity mapping error:", err);
      setError(
        err.response?.data?.message || err.message || "Something went wrong"
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Category-level check state ────────────────────────────
  const getCategoryState = (cat) => {
    const subIds = cat.subactivities.map((s) => s.id);
    const checkedCount = subIds.filter((id) => selectedIds.has(id)).length;
    if (checkedCount === 0) return "none";
    if (checkedCount === subIds.length) return "all";
    return "some";
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box"
        style={{ maxWidth: "560px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div>
            <div className="modal-title">Map Activities</div>
            <div className="modal-subtitle" style={{ fontSize: "13px", color: "#888", marginTop: "2px" }}>
              {data?.year_label}&nbsp;&nbsp;
              <span>
                <strong>[Start: </strong>
                {data?.start_date
                  ? new Date(data.start_date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—"}
                <strong> &nbsp;End: </strong>
                {data?.end_date
                  ? new Date(data.end_date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—"}
                <strong>]</strong>
              </span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body — scrollable */}
        <div className="modal-body" style={{ overflowY: "auto", flex: 1 }}>

          {/* Select All row */}
          {!loadingActivities && allSubIds.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                paddingBottom: "10px",
                borderBottom: "1px solid var(--border, #e5e7eb)",
              }}
            >
              <span className="org-field-label" style={{ margin: 0 }}>
                Select Activities
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "var(--primary, #2563eb)",
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={handleToggleAll}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </span>
            </div>
          )}

          {loadingActivities ? (
            <p className="empty-text">Loading activities...</p>
          ) : categories.length === 0 ? (
            <p className="empty-text">No activities available.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {categories.map((cat) => {
                const catState = getCategoryState(cat);
                const isExpanded = expandedCategories.has(cat.id);

                return (
                  <div
                    key={cat.id}
                    style={{
                      border: "1px solid var(--border, #e5e7eb)",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    {/* Category header row */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 14px",
                        background: catState === "all"
                          ? "var(--primary-light, #eff6ff)"
                          : catState === "some"
                          ? "var(--bg-subtle, #f5f7fa)"
                          : "var(--bg-subtle, #f5f7fa)",
                        gap: "10px",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {/* Category checkbox */}
                      <input
                        type="checkbox"
                        className="location-checkbox"
                        checked={catState === "all"}
                        ref={(el) => {
                          if (el) el.indeterminate = catState === "some";
                        }}
                        onChange={() => handleCategoryToggle(cat)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ flexShrink: 0 }}
                      />

                      {/* Category name — click to expand */}
                      <span
                        style={{ fontWeight: 600, fontSize: "14px", flex: 1, color: "#333" }}
                        onClick={() => toggleExpand(cat.id)}
                      >
                        {cat.name}
                      </span>

                      {/* Sub-activity count badge */}
                      <span
                        style={{
                          fontSize: "11px",
                          background: "#e5e7eb",
                          borderRadius: "10px",
                          padding: "2px 8px",
                          color: "#555",
                        }}
                      >
                        {cat.subactivities.filter((s) => selectedIds.has(s.id)).length}
                        /{cat.subactivities.length}
                      </span>

                      {/* Expand/collapse arrow */}
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                        onClick={() => toggleExpand(cat.id)}
                      >
                        ▼
                      </span>
                    </div>

                    {/* Sub-activities */}
                    {isExpanded && (
                      <div style={{ padding: "6px 14px 10px 14px" }}>
                        {cat.subactivities.map((sub) => (
                          <label
                            key={sub.id}
                            className="location-row"
                            style={{ paddingLeft: "8px" }}
                          >
                            <input
                              type="checkbox"
                              className="location-checkbox"
                              checked={selectedIds.has(sub.id)}
                              onChange={() => handleCheckbox(sub.id)}
                            />
                            <span className="location-text" style={{ flex: 1 }}>
                              {sub.name}
                            </span>
                            {sub.scope && sub.scope !== "none" && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  background: "#dbeafe",
                                  color: "#1d4ed8",
                                  borderRadius: "6px",
                                  padding: "1px 7px",
                                  marginLeft: "8px",
                                  textTransform: "uppercase",
                                  fontWeight: 600,
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {sub.scope}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div style={{ marginTop: "12px", fontSize: "12px", color: "#666" }}>
              {selectedIds.size} activit{selectedIds.size !== 1 ? "ies" : "y"} selected
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ marginTop: "12px", color: "var(--error, #dc2626)", fontSize: "13px" }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="org-btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="org-btn-save"
            onClick={handleSubmit}
            disabled={saving || loadingActivities}
          >
            {saving ? "Saving..." : "Save Mapping"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityMappingModal;